import React, { createContext, useContext, useState, useEffect } from 'react';
import { dbOperations, isSupabaseConnected } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cities, setCities] = useState([]);
  const [isConnectedToSupabase, setIsConnectedToSupabase] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);

  useEffect(() => {
    // Check Supabase connection status
    setIsConnectedToSupabase(isSupabaseConnected());
    
    // Initialize the app
    initializeApp();
  }, []);

  const initializeApp = async () => {
    try {
      // Check for existing session
      const savedUser = localStorage.getItem('cityshot_user');
      if (savedUser) {
        try {
          const userData = JSON.parse(savedUser);
          setUser(userData);
        } catch (error) {
          console.error('Error parsing saved user:', error);
          localStorage.removeItem('cityshot_user');
        }
      }

      // Check for magic link in URL
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      
      if (token) {
        await handleMagicLinkLogin(token);
      }

      // Load cities from database or localStorage
      await loadCities();
      
      // Initialize demo data if no cities exist
      const currentCities = await dbOperations.getCities();
      if (currentCities.length === 0) {
        await initializeDemoData();
      }
    } catch (error) {
      console.error('Error initializing app:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLinkLogin = async (token) => {
    try {
      const result = await dbOperations.validateMagicLink(token);
      
      if (result.valid) {
        // Clear the URL parameters
        window.history.replaceState({}, document.title, window.location.pathname + window.location.hash);
        
        // Handle super admin
        if (result.email === 'cityshot@clickbooster.app') {
          const userData = {
            id: 'super-admin',
            email: result.email,
            cityName: 'Super Admin',
            role: 'superadmin',
            subscription: 'enterprise',
            onboarded: true,
            email_verified: true
          };
          
          localStorage.setItem('cityshot_user', JSON.stringify(userData));
          setUser(userData);
          
          // Redirect to admin dashboard
          setTimeout(() => {
            window.location.href = window.location.origin + '/#/admin';
          }, 100);
          return;
        }
        
        // Handle city users
        if (result.city_id) {
          const cities = await dbOperations.getCities();
          const city = cities.find(c => c.id === result.city_id);
          
          if (city) {
            const userData = {
              id: city.id,
              email: city.email,
              cityName: city.city_name,
              role: 'city_admin',
              subscription: city.subscription_status,
              plan: city.plan,
              onboarded: city.onboarded || false,
              email_verified: result.purpose === 'verify_email' ? true : city.email_verified
            };
            
            // Update email verification status if this was an email verification
            if (result.purpose === 'verify_email') {
              await dbOperations.updateCity(city.id, { email_verified: true });
              userData.email_verified = true;
            }
            
            localStorage.setItem('cityshot_user', JSON.stringify(userData));
            setUser(userData);
            
            // Redirect based on onboarding status
            if (!userData.onboarded) {
              setTimeout(() => {
                window.location.href = window.location.origin + '/#/onboarding';
              }, 100);
            } else {
              setTimeout(() => {
                window.location.href = window.location.origin + '/#/dashboard';
              }, 100);
            }
          }
        }
      } else {
        console.error('Invalid magic link');
      }
    } catch (error) {
      console.error('Magic link login error:', error);
    }
  };

  const loadCities = async () => {
    try {
      const citiesData = await dbOperations.getCities();
      setCities(citiesData);
    } catch (error) {
      console.error('Error loading cities:', error);
      setCities([]);
    }
  };

  const initializeDemoData = async () => {
    try {
      // Create demo city
      const demoCity = {
        id: 'demo-city-123',
        email: 'musterstadt@clickbooster.app',
        city_name: 'Musterstadt',
        subscription_status: 'active',
        plan: 'professional',
        email_verified: true,
        onboarded: true,
        created_at: new Date().toISOString()
      };

      const savedCity = await dbOperations.addCity(demoCity);
      
      // Create demo configuration
      const demoConfig = {
        primaryColor: '#3B82F6',
        secondaryColor: '#EF4444',
        textColor: '#FFFFFF',
        logo: '',
        secondImage: '',
        customText: 'Willkommen in Musterstadt!',
        showLocation: true,
        showGreeting: true,
        showAdditionalText: false,
        greetingText: 'Viele Grüße aus',
        additionalText: 'Schloss Musterstein',
        textBackground: true,
        logoPosition: { x: '2%', y: '2%' },
        logoSize: '20%',
        logoOpacity: 1,
        secondImagePosition: { x: 'right', y: '2%' },
        secondImageSize: '20%',
        secondImageOpacity: 1,
        textPosition: { x: 'center', y: 'bottom' },
        textSize: '1.8vmin',
        textOpacity: 0.9,
        textStyle: 'bold',
        fontFamily: 'Arial',
        locationPosition: { x: 'center', y: 'bottom' },
        locationSize: '2.2vmin',
        locationOpacity: 1,
        locationFontFamily: 'Arial',
        locationColor: '#FFFFFF',
        locationStyle: 'bold',
        additionalTextPosition: { x: 'center', y: 'bottom' },
        additionalTextSize: '1.6vmin',
        additionalTextOpacity: 0.9,
        additionalFontFamily: 'Arial',
        additionalTextColor: '#FFFFFF',
        additionalTextStyle: 'normal',
        _timestamp: Date.now()
      };

      await dbOperations.saveConfig(savedCity.id, demoConfig);
      
      // Reload cities to include the new demo city
      await loadCities();
      
      console.log('✅ Demo data initialized successfully');
    } catch (error) {
      console.error('Error initializing demo data:', error);
    }
  };

  const signUp = async (signUpData) => {
    try {
      const { 
        cityName, email, contactPerson, phoneNumber, country, website, 
        expectedVisitors, selectedTier, finalPrice, couponApplied, discount 
      } = signUpData;

      // Create new city data
      const newCityData = {
        email,
        city_name: cityName,
        contact_person: contactPerson,
        phone_number: phoneNumber,
        country,
        website,
        expected_visitors: expectedVisitors,
        subscription_status: finalPrice === 0 ? 'active' : 'trial',
        plan: selectedTier,
        price_paid: finalPrice,
        coupon_used: couponApplied,
        discount_applied: discount,
        email_verified: false, // Will be verified via magic link
        onboarded: false
      };

      // Save to database
      const savedCity = await dbOperations.addCity(newCityData);
      
      // Use coupon if applied
      if (couponApplied) {
        await dbOperations.useCoupon(couponApplied);
      }

      // Create user object
      const userData = {
        id: savedCity.id,
        email: savedCity.email,
        cityName: savedCity.city_name,
        role: 'city_admin',
        subscription: savedCity.subscription_status,
        plan: savedCity.plan,
        onboarded: false,
        email_verified: false
      };

      // Set pending user for email verification
      setPendingUser(userData);

      // Send verification magic link
      await dbOperations.createMagicLink(email, 'verify_email');

      // Create default configuration for new city
      const defaultConfig = {
        primaryColor: '#3B82F6',
        secondaryColor: '#EF4444',
        textColor: '#FFFFFF',
        logo: '',
        secondImage: '',
        customText: `Willkommen in ${cityName}!`,
        showLocation: true,
        showGreeting: true,
        showAdditionalText: false,
        greetingText: 'Viele Grüße aus',
        additionalText: '',
        textBackground: true,
        logoPosition: { x: '2%', y: '2%' },
        logoSize: '20%',
        logoOpacity: 1,
        secondImagePosition: { x: 'right', y: '2%' },
        secondImageSize: '20%',
        secondImageOpacity: 1,
        textPosition: { x: 'center', y: 'bottom' },
        textSize: '1.8vmin',
        textOpacity: 0.9,
        textStyle: 'bold',
        fontFamily: 'Arial',
        locationPosition: { x: 'center', y: 'bottom' },
        locationSize: '2.2vmin',
        locationOpacity: 1,
        locationFontFamily: 'Arial',
        locationColor: '#FFFFFF',
        locationStyle: 'bold',
        additionalTextPosition: { x: 'center', y: 'bottom' },
        additionalTextSize: '1.6vmin',
        additionalTextOpacity: 0.9,
        additionalFontFamily: 'Arial',
        additionalTextColor: '#FFFFFF',
        additionalTextStyle: 'normal',
        _timestamp: Date.now()
      };

      await dbOperations.saveConfig(savedCity.id, defaultConfig);
      
      // Reload cities
      await loadCities();
      
      // Show email verification modal
      setShowEmailVerification(true);
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('Sign up error:', error);
      return { success: false, error: error.message };
    }
  };

  const sendMagicLink = async (email) => {
    try {
      const result = await dbOperations.sendMagicLink(email);
      return result;
    } catch (error) {
      console.error('Magic link error:', error);
      return { success: false, error: error.message };
    }
  };

  const validateMagicLink = async (token) => {
    try {
      const result = await dbOperations.validateMagicLink(token);
      
      if (result.valid && result.city_id) {
        // Find city data
        const cities = await dbOperations.getCities();
        const city = cities.find(c => c.id === result.city_id);
        
        if (city) {
          const userData = {
            id: city.id,
            email: city.email,
            cityName: city.city_name,
            role: 'city_admin',
            subscription: city.subscription_status,
            plan: city.plan,
            onboarded: city.onboarded || false,
            email_verified: result.purpose === 'verify_email' ? true : city.email_verified
          };
          
          // Update email verification status if this was an email verification
          if (result.purpose === 'verify_email') {
            await dbOperations.updateCity(city.id, { email_verified: true });
          }
          
          localStorage.setItem('cityshot_user', JSON.stringify(userData));
          setUser(userData);
          
          return { success: true, user: userData, purpose: result.purpose };
        }
      } else if (result.valid && result.email === 'cityshot@clickbooster.app') {
        // Super admin login
        const userData = {
          id: 'super-admin',
          email: result.email,
          cityName: 'Super Admin',
          role: 'superadmin',
          subscription: 'enterprise',
          onboarded: true,
          email_verified: true
        };
        
        localStorage.setItem('cityshot_user', JSON.stringify(userData));
        setUser(userData);
        
        return { success: true, user: userData, purpose: result.purpose };
      }
      
      return { success: false, error: 'Invalid or expired link' };
    } catch (error) {
      console.error('Magic link validation error:', error);
      return { success: false, error: error.message };
    }
  };

  const updateUserOnboarding = async (onboarded) => {
    try {
      if (user) {
        const updatedUser = { ...user, onboarded };
        localStorage.setItem('cityshot_user', JSON.stringify(updatedUser));
        setUser(updatedUser);

        // Update in database if connected
        await dbOperations.updateCity(user.id, { onboarded });
      }
    } catch (error) {
      console.error('Error updating onboarding status:', error);
    }
  };

  const logout = async () => {
    try {
      await dbOperations.signOut();
      localStorage.removeItem('cityshot_user');
      setUser(null);
      setPendingUser(null);
      setShowEmailVerification(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const addCity = async (cityData) => {
    try {
      const newCityData = {
        ...cityData,
        email_verified: false,
        onboarded: false
      };

      const savedCity = await dbOperations.addCity(newCityData);
      
      // Create default configuration for new city
      const defaultConfig = {
        primaryColor: '#3B82F6',
        secondaryColor: '#EF4444',
        textColor: '#FFFFFF',
        logo: '',
        secondImage: '',
        customText: `Willkommen in ${cityData.city_name}!`,
        showLocation: true,
        showGreeting: true,
        showAdditionalText: false,
        greetingText: 'Viele Grüße aus',
        additionalText: '',
        textBackground: true,
        logoPosition: { x: '2%', y: '2%' },
        logoSize: '20%',
        logoOpacity: 1,
        secondImagePosition: { x: 'right', y: '2%' },
        secondImageSize: '20%',
        secondImageOpacity: 1,
        textPosition: { x: 'center', y: 'bottom' },
        textSize: '1.8vmin',
        textOpacity: 0.9,
        textStyle: 'bold',
        fontFamily: 'Arial',
        locationPosition: { x: 'center', y: 'bottom' },
        locationSize: '2.2vmin',
        locationOpacity: 1,
        locationFontFamily: 'Arial',
        locationColor: '#FFFFFF',
        locationStyle: 'bold',
        additionalTextPosition: { x: 'center', y: 'bottom' },
        additionalTextSize: '1.6vmin',
        additionalTextOpacity: 0.9,
        additionalFontFamily: 'Arial',
        additionalTextColor: '#FFFFFF',
        additionalTextStyle: 'normal',
        _timestamp: Date.now()
      };

      await dbOperations.saveConfig(savedCity.id, defaultConfig);
      
      // Reload cities
      await loadCities();
      
      return savedCity;
    } catch (error) {
      console.error('Error adding city:', error);
      throw error;
    }
  };

  const updateCity = async (cityId, updatedData) => {
    try {
      const updatedCity = await dbOperations.updateCity(cityId, updatedData);
      
      // Update current user if it's the same city
      if (user?.id === cityId) {
        const updatedUser = { ...user, ...updatedData };
        localStorage.setItem('cityshot_user', JSON.stringify(updatedUser));
        setUser(updatedUser);
      }
      
      // Reload cities
      await loadCities();
      
      return updatedCity;
    } catch (error) {
      console.error('Error updating city:', error);
      throw error;
    }
  };

  const deleteCity = async (cityId) => {
    try {
      await dbOperations.deleteCity(cityId);
      
      // If the current user is the deleted city, log them out
      if (user?.id === cityId) {
        await logout();
      }
      
      // Reload cities
      await loadCities();
      
      return { success: true };
    } catch (error) {
      console.error('Error deleting city:', error);
      throw error;
    }
  };

  const isSuperAdmin = (user) => {
    return user?.email === 'cityshot@clickbooster.app';
  };

  const value = {
    user,
    cities,
    logout,
    signUp,
    loading,
    isSuperAdmin: user ? isSuperAdmin(user) : false,
    sendMagicLink,
    validateMagicLink,
    addCity,
    updateCity,
    deleteCity,
    updateUserOnboarding,
    isConnectedToSupabase,
    showEmailVerification,
    setShowEmailVerification,
    pendingUser,
    // Database operations for direct access
    dbOperations
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;